import React, { useCallback, useEffect } from "react";
import { useLocation } from "react-router";
import { Helmet } from "react-helmet";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import useAEEditor from "../../../hooks/useAEEditor";
import { setReplaceMediaContent, startThumbImageUploadProcess } from "../../../actions/publishActions";
import { AdobeExpressUtils } from "./utils";
import { mediaActionKeys } from "../../../constants/mediaConstants";
import { showLoadingProjectMessage } from "../../../actions/notificationActions";
import { updateAdobeExpressEditorModal } from "../../../actions/sessionActions";

const AdobeExpressFullEditor = () => {
    const dispatch = useDispatch() as any;
    const location = useLocation();
    const { t } = useTranslation();

    const { ccEverywhere } = useSelector((state) => (state as any).publish);
    const { defaultAccountId } = useSelector((state) => (state as any).session);

	const { uploadToPlatformCallback } = useAEEditor({
		afterJobFunc: ({ replaceMediaId }) => {
			if (replaceMediaId) {
				dispatch(setReplaceMediaContent({ mediaId: replaceMediaId, isFromAEReplacing: true }));
			}
            dispatch(showLoadingProjectMessage(t("ADOBE_EXPRESS_FULL_EDITOR_NOTIFICATION")));
            setTimeout(() => {
                dispatch(showLoadingProjectMessage(false));
                window.close();
            }, 5000);
		}
	});

    const handleThumbImageUpload = (selectedFile: File) => {
	    const searchParams = new URLSearchParams(location.search);
        const updateToAssetId = searchParams.get("updateToAssetId") ?? "";
        if (!updateToAssetId) {
            return;
        }
        dispatch(startThumbImageUploadProcess(defaultAccountId, selectedFile, updateToAssetId)).then((_res: any) => {
            setTimeout(() => {
                dispatch(showLoadingProjectMessage(false));
                window.close();
            }, 3000);
        });
    };

    const uploadImageByUrl = async ({ mediaUrl, projectId, mediaType }: any) => {
        const blodFile = await fetch(mediaUrl)
            .then((r) => r.blob())
            .then(
                (blobFile) =>
                    new File(
                        [blobFile],
                        `${AdobeExpressUtils.getMediaIdFromProjectId(projectId)}.${mediaType.split('/')[1]}`,
                        { type: mediaType }
                    )
            );
        handleThumbImageUpload(blodFile);
    };

    const onPublish = (publishParams: any) => {
        dispatch(showLoadingProjectMessage(t("ADOBE_EXPRESS_FULL_EDITOR_NOTIFICATION")));
	    const searchParams = new URLSearchParams(location.search);
        const updateToAssetId = searchParams.get("updateToAssetId") ?? "";
        if (updateToAssetId) {
            const asset = publishParams?.asset[0];
            if (asset) {
                const mediaUrl = asset.data;
                const projectId = publishParams.projectId;
                uploadImageByUrl({
                    mediaUrl,
                    projectId,
                    mediaType: asset.fileType,
                }).then(() => {
                    dispatch(updateAdobeExpressEditorModal(false));
                });
            }
        } else {
            dispatch(showLoadingProjectMessage(false));
            window.close();
        }
    };

    const createAdobeExpressDesign = useCallback((dataUrl?: string, mediaName?: string, isImage?: boolean, catalogId?: string) => {
        const searchParams = new URLSearchParams(location.search);
        const updateToAssetId = searchParams.get("updateToAssetId") ?? "";

        const actionCallback = updateToAssetId ? {
            ...uploadToPlatformCallback,
            onPublish: (_intent: any, publishParams: any, _mediaName?: string, _replaceMediaContent?: object) => {
                onPublish(publishParams);
            }
        } : uploadToPlatformCallback;

        AdobeExpressUtils.createNewProject({
            ccEverywhere,
            actionCallback,
            dataUrl,
            mediaName,
            catalogId,
            setLoadingProgress: (value: number | false) => dispatch(showLoadingProjectMessage(value)),
            dispatch,
            isFullScreenEditor: true,
            mediaType: isImage ? 'image' : 'video'
        });
        dispatch(showLoadingProjectMessage(!!dataUrl ? true : 'no-message'));
        dispatch(updateAdobeExpressEditorModal(true));
    }, [ccEverywhere, dispatch, location.search, uploadToPlatformCallback]);

    useEffect(() => {
        if (!ccEverywhere) {
            return;
        }

	    const searchParams = new URLSearchParams(location.search);
        const action = searchParams.get("action") ?? "";
        const dataUrl = searchParams.get("dataUrl") ?? "";
        const projectId = searchParams.get("projectId") ?? "";
        const mediaName = searchParams.get("mediaName") ?? "";
        const catalogId = searchParams.get("catalogId") ?? "";
        const replaceMediaId = searchParams.get("replaceMediaId") ?? "";
        const replaceMediaAssetId = searchParams.get("replaceMediaAssetId") ?? "";
        const updateToAssetId = searchParams.get("updateToAssetId") ?? "";

        if ((!dataUrl && !projectId) || !action) {
            createAdobeExpressDesign(dataUrl, mediaName, searchParams.get("isImage") === "true", catalogId);
            return;
        }

        const actionCallback = updateToAssetId ? {
            ...uploadToPlatformCallback,
            onPublish: (_intent: any, publishParams: any, _mediaName?: string, _replaceMediaContent?: object) => {
                onPublish(publishParams);
            }
        } : uploadToPlatformCallback;

        if (action.includes('-with-ae')) {
            const aeConfigParams = {
                ccEverywhere,
                dataUrl,
                actionCallback,
                mediaName,
                catalogId,
                setLoadingProgress: (value: number | false) => dispatch(showLoadingProjectMessage(value)),
                dispatch,
                isFullScreenEditor: true
            };

            switch (action) {
                case mediaActionKeys.editWithAE:
                    createAdobeExpressDesign(dataUrl, mediaName, searchParams.get("isImage") === "true", catalogId);
                    break;
                case mediaActionKeys.trimVideoWithAE:
                    AdobeExpressUtils.trimVideo(aeConfigParams);
                    break;
                case mediaActionKeys.resizeVideoWithAE:
                    AdobeExpressUtils.resizeVideo(aeConfigParams);
                    break;
                case mediaActionKeys.resizeImageWithAE:
                    AdobeExpressUtils.resizeImage(aeConfigParams);
                    break;
            }
        }

        if (action === mediaActionKeys.openAEProject || action === mediaActionKeys.openAndReplaceAEProject) {
            const replaceMediaContent = replaceMediaId ? { mediaId: replaceMediaId, assetId: replaceMediaAssetId } : undefined;
            projectId && AdobeExpressUtils.editDesign({
                ccEverywhere,
                projectId,
                actionCallback,
                mediaName,
                catalogId,
                replaceMediaContent,
                setLoadingProgress: (value: number | false) => dispatch(showLoadingProjectMessage(value)),
                dispatch,
                isFullScreenEditor: true,
                mediaType: searchParams.get("isImage") === "true" ? 'image' : 'video'
            });

            dispatch(showLoadingProjectMessage(true));
            dispatch(updateAdobeExpressEditorModal(true));
        }
    }, [ccEverywhere, createAdobeExpressDesign, dispatch, location.search, uploadToPlatformCallback]);

	return (<>
        <Helmet>
            <title>Adobe Express</title>
        </Helmet>
		<div style={{ backgroundColor: '#ffffff' }} />
    </>);
};

export default AdobeExpressFullEditor;
